import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CustomPageContext, TabbedPage } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { InlineCopyInput, Loading, PageHeader } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardAccountRepository } from '~/repositories/CardAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner, Line, Text } from '~/styles';

interface Params {
  id: string;
}

export interface CardAccountProps {
  cardAccount: {
    id: string;
    cardProgramId: string;
    status: string;
    bankAccountId?: string;
    loanId?: string;
    createdAt?: Date;
  };
}

const TabNavigation = styled(TabbedPage)`
  margin: 0;
`;

const CardAccountHeaderRow = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InlineCopyInputWrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: auto;
  }
`;

export const PageCardAccount: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;

  const {
    data: cardAccount,
    isLoading,
    error: cardAccountError,
  } = useQuery({
    queryKey: ['card-account', id, currentPlatform?.id, isSandbox],
    queryFn: () => CardAccountRepository.get(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (cardAccountError) {
      navigate(ROUTE.CARD_ACCOUNTS);
      addDangerNotification({
        content: (cardAccountError as any).message || 'Failed to load card account',
      });
    }
  }, [cardAccountError, navigate, addDangerNotification]);

  const outletContext = useMemo(() => (cardAccount ? { cardAccount } : undefined), [cardAccount]);

  return (
    <Loading isLoading={isLoading} fullHeight>
      <PageHeader
        text="Card Accounts"
        backPath={ROUTE.CARD_ACCOUNTS}
        tabs={<TabNavigation entries={entries} />}
        backButton
      />
      {id && cardAccount && (
        <>
          <Inner pt={12} px="var(--page-margin)" pb={16}>
            <Flex flexDirection="column" gap={12}>
              <CardAccountHeaderRow>
                <Text as="h2" size={16} letterSpacing="tight" weight="medium" color="foreground">
                  Card Account
                </Text>

                <InlineCopyInputWrapper>
                  <InlineCopyInput label="Card Account ID" value={cardAccount.id} minWidth="300px" isMonospaced />
                </InlineCopyInputWrapper>
              </CardAccountHeaderRow>
            </Flex>
          </Inner>
          <Line />
        </>
      )}
      {outletContext && <Outlet context={outletContext} />}
    </Loading>
  );
};
